import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    CircularProgress,
    Chip,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    Grid,
    Autocomplete
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { getFirestore, collection, getDocs, query, orderBy as firestoreOrderBy } from 'firebase/firestore';
import moment from 'moment';

const firestore = getFirestore();

function ListRemitos() {
    const [remitos, setRemitos] = useState([]);
    const [cuadrillas, setCuadrillas] = useState([]);
    const [obras, setObras] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCuadrilla, setFilterCuadrilla] = useState(null);
    const [filterObra, setFilterObra] = useState(null);
    const [detailDialog, setDetailDialog] = useState({ open: false, remito: null });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            
            // Cargar remitos
            const remitosQuery = query(
                collection(firestore, 'remitos_huawei'),
                firestoreOrderBy('timestamp', 'desc')
            );
            const remitosSnapshot = await getDocs(remitosQuery);
            const remitosData = [];
            remitosSnapshot.forEach((docSnap) => {
                remitosData.push({ ...docSnap.data(), docId: docSnap.id });
            });
            setRemitos(remitosData);

            // Cargar cuadrillas
            const cuadrillasQuery = query(
                collection(firestore, 'cuadrillas_huawei'),
                firestoreOrderBy('nombre', 'asc')
            );
            const cuadrillasSnapshot = await getDocs(cuadrillasQuery);
            const cuadrillasData = [];
            cuadrillasSnapshot.forEach((docSnap) => {
                cuadrillasData.push({ ...docSnap.data(), docId: docSnap.id });
            });
            setCuadrillas(cuadrillasData);

            // Cargar obras
            const obrasQuery = query(
                collection(firestore, 'obras_huawei'),
                firestoreOrderBy('nombre', 'asc')
            );
            const obrasSnapshot = await getDocs(obrasQuery);
            const obrasData = [];
            obrasSnapshot.forEach((docSnap) => {
                obrasData.push({ ...docSnap.data(), docId: docSnap.id });
            });
            setObras(obrasData);
            
        } catch (error) {
            console.error('Error al cargar datos:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetail = (remito) => {
        setDetailDialog({ open: true, remito });
    };

    const handleCloseDetail = () => {
        setDetailDialog({ open: false, remito: null });
    };

    const filteredRemitos = remitos.filter(remito => {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = (
            remito.tipoRemito?.toLowerCase().includes(searchLower) ||
            remito.fecha?.toLowerCase().includes(searchLower) ||
            remito.cuadrillaOrigen?.toLowerCase().includes(searchLower) ||
            remito.cuadrillaOrigenNombre?.toLowerCase().includes(searchLower) ||
            remito.cuadrillaDestino?.toLowerCase().includes(searchLower) ||
            remito.cuadrillaDestinoNombre?.toLowerCase().includes(searchLower) ||
            remito.obraOrigenNombre?.toLowerCase().includes(searchLower) ||
            remito.obraDestinoNombre?.toLowerCase().includes(searchLower)
        );
        
        const matchesCuadrilla = !filterCuadrilla || 
            remito.cuadrillaOrigen === filterCuadrilla.docId ||
            remito.cuadrillaDestino === filterCuadrilla.docId ||
            remito.cuadrillaOrigenNombre === filterCuadrilla.numero ||
            remito.cuadrillaDestinoNombre === filterCuadrilla.numero;
        
        const matchesObra = !filterObra || 
            remito.obraOrigen === filterObra.docId ||
            remito.obraDestino === filterObra.docId ||
            remito.obraOrigenNombre === filterObra.nombre ||
            remito.obraDestinoNombre === filterObra.nombre;
        
        return matchesSearch && matchesCuadrilla && matchesObra;
    });

    if (loading) {
        return (
            <Container maxWidth="xl">
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl">
            <Box sx={{ mt: 4, mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" component="h1">
                        Remitos - Huawei
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        href="/huawei/remito-add"
                    >
                        Nuevo Remito
                    </Button>
                </Box>
                
                <Box sx={{ mb: 3 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Buscar por tipo, fecha, origen o destino"
                                variant="outlined"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Buscar remitos..."
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Autocomplete
                                options={cuadrillas}
                                getOptionLabel={(option) => option.usuario ? `${option.usuario} - ${option.numero || ''}` : (option.numero || '')}
                                value={filterCuadrilla}
                                onChange={(e, newValue) => setFilterCuadrilla(newValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Filtrar por Cuadrilla"
                                        placeholder="Seleccionar cuadrilla..."
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Autocomplete
                                options={obras}
                                getOptionLabel={(option) => option.nombre || ''}
                                value={filterObra}
                                onChange={(e, newValue) => setFilterObra(newValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Filtrar por Obra"
                                        placeholder="Seleccionar obra..."
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Box sx={{ mb: 2 }}>
                    <Chip label={`Total: ${filteredRemitos.length} remitos`} color="primary" />
                </Box>

                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>Fecha</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Origen</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Destino</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Materiales</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredRemitos.map((remito) => (
                                <TableRow 
                                    key={remito.docId}
                                    sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}
                                >
                                    <TableCell>{remito.fecha}</TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={remito.tipoRemito}
                                            color={
                                                remito.tipoRemito === 'Ingreso' ? 'success' : 
                                                remito.tipoRemito === 'Egreso' ? 'error' : 
                                                'info'
                                            }
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {remito.tipoRemito === 'Ingreso' 
                                            ? remito.cuadrillaOrigen 
                                            : remito.tipoRemito === 'Egreso'
                                            ? `Cuadrilla: ${remito.usuarioCuadrillaOrigen ? `${remito.usuarioCuadrillaOrigen} - ${remito.cuadrillaOrigenNombre || ''}` : (remito.cuadrillaOrigenNombre || remito.cuadrillaOrigen)}`
                                            : `Obra: ${remito.obraOrigenNombre || remito.obraOrigen}`}
                                    </TableCell>
                                    <TableCell>
                                        {remito.tipoRemito === 'Ingreso' 
                                            ? `Cuadrilla: ${remito.usuarioCuadrillaDestino ? `${remito.usuarioCuadrillaDestino} - ${remito.cuadrillaDestinoNombre || ''}` : (remito.cuadrillaDestinoNombre || remito.cuadrillaDestino)}` 
                                            : remito.tipoRemito === 'Egreso'
                                            ? `Cuadrilla: ${remito.usuarioCuadrillaDestino ? `${remito.usuarioCuadrillaDestino} - ${remito.cuadrillaDestinoNombre || ''}` : (remito.cuadrillaDestinoNombre || remito.cuadrillaDestino)}`
                                            : `Obra: ${remito.obraDestinoNombre || remito.obraDestino}`}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                        <Chip 
                                            label={remito.materiales?.length || 0}
                                            size="small"
                                            color="default"
                                        />
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                        <IconButton
                                            color="primary"
                                            size="small"
                                            onClick={() => handleViewDetail(remito)}
                                            title="Ver detalle"
                                        >
                                            <VisibilityIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {filteredRemitos.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                            {searchTerm ? `No se encontraron remitos para "${searchTerm}"` : 'No hay remitos registrados. Crea un nuevo remito para comenzar.'}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Dialog de Detalle */}
            <Dialog open={detailDialog.open} onClose={handleCloseDetail} maxWidth="md" fullWidth>
                <DialogTitle>
                    Detalle del Remito - {detailDialog.remito?.fecha}
                </DialogTitle>
                <DialogContent>
                    {detailDialog.remito && (
                        <Box>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                Información General
                            </Typography>
                            <Box sx={{ mb: 2 }}>
                                <Typography><strong>Tipo:</strong> {detailDialog.remito.tipoRemito}</Typography>
                                <Typography><strong>Fecha:</strong> {detailDialog.remito.fecha}</Typography>
                                {detailDialog.remito.tipoRemito === 'Ingreso' ? (
                                    <>
                                        <Typography><strong>Cuadrilla Origen:</strong> {detailDialog.remito.cuadrillaOrigen}</Typography>
                                        <Typography><strong>Cuadrilla Destino:</strong> {detailDialog.remito.usuarioCuadrillaDestino ? `${detailDialog.remito.usuarioCuadrillaDestino} - ${detailDialog.remito.cuadrillaDestinoNombre || ''}` : (detailDialog.remito.cuadrillaDestinoNombre || detailDialog.remito.cuadrillaDestino)}</Typography>
                                        <Typography><strong>Obra Destino:</strong> {detailDialog.remito.obraDestinoNombre || detailDialog.remito.obraDestino}</Typography>
                                    </>
                                ) : detailDialog.remito.tipoRemito === 'Egreso' ? (
                                    <>
                                        <Typography><strong>Cuadrilla Origen:</strong> {detailDialog.remito.usuarioCuadrillaOrigen ? `${detailDialog.remito.usuarioCuadrillaOrigen} - ${detailDialog.remito.cuadrillaOrigenNombre || ''}` : (detailDialog.remito.cuadrillaOrigenNombre || detailDialog.remito.cuadrillaOrigen)}</Typography>
                                        <Typography><strong>Cuadrilla Destino:</strong> {detailDialog.remito.usuarioCuadrillaDestino ? `${detailDialog.remito.usuarioCuadrillaDestino} - ${detailDialog.remito.cuadrillaDestinoNombre || ''}` : (detailDialog.remito.cuadrillaDestinoNombre || detailDialog.remito.cuadrillaDestino)}</Typography>
                                        <Typography><strong>Obra Destino:</strong> {detailDialog.remito.obraDestinoNombre || detailDialog.remito.obraDestino}</Typography>
                                    </>
                                ) : (
                                    <>
                                        <Typography><strong>Obra Origen:</strong> {detailDialog.remito.obraOrigenNombre || detailDialog.remito.obraOrigen}</Typography>
                                        <Typography><strong>Obra Destino:</strong> {detailDialog.remito.obraDestinoNombre || detailDialog.remito.obraDestino}</Typography>
                                        <Typography><strong>Cuadrilla Origen:</strong> {detailDialog.remito.usuarioCuadrillaOrigen ? `${detailDialog.remito.usuarioCuadrillaOrigen} - ${detailDialog.remito.cuadrillaOrigenNombre || ''}` : (detailDialog.remito.cuadrillaOrigenNombre || detailDialog.remito.cuadrillaOrigen)}</Typography>
                                        <Typography><strong>Cuadrilla Destino:</strong> {detailDialog.remito.usuarioCuadrillaDestino ? `${detailDialog.remito.usuarioCuadrillaDestino} - ${detailDialog.remito.cuadrillaDestinoNombre || ''}` : (detailDialog.remito.cuadrillaDestinoNombre || detailDialog.remito.cuadrillaDestino)}</Typography>
                                    </>
                                )}
                                {detailDialog.remito.observaciones && (
                                    <Typography><strong>Observaciones:</strong> {detailDialog.remito.observaciones}</Typography>
                                )}
                            </Box>

                            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                                Materiales
                            </Typography>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Descripción</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Cantidad</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Unidad</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Observaciones</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {detailDialog.remito.materiales?.map((material, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{material.codigo}</TableCell>
                                                <TableCell>{material.descripcion}</TableCell>
                                                <TableCell sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                                                    {material.cantidad}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip label={material.unidad} size="small" />
                                                </TableCell>
                                                <TableCell>{material.observaciones || '-'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDetail}>
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default ListRemitos;
